import React from "react";
import { infiniteScrollHook } from "../../../hooks/infiniteScrollHook";
import SimpleSpinner from "../../loader";
import EmptyState from "../../../newComponents/emptystate";
import { GetImages } from "../../../utils/GetImages";

export interface OrgScopedColumn {
  key: string;
  label: string;
  // Optional custom renderer; receives the whole row.
  render?: (row: any) => React.ReactNode;
}

interface OrgScopedListProps {
  organizationId: number;
  // A generated client list method whose FIRST positional arg is the org id and that follows the
  // standard (orgId, pageSize, pageNumber, ...) signature (e.g. PropertyService.getApiPropertyOrganizationlist).
  httpFetchDataFn: (...args: any[]) => any;
  columns: OrgScopedColumn[];
  emptyHeading?: string;
}

/**
 * A thin, reusable org-scoped table for the Platform portal. It drives the shared
 * `infiniteScrollHook` with an explicit `overrideReqId` (the selected organization id) since a
 * Platform token has no `organization_id` in sessionStorage. Uses the plain `.../organizationlist`
 * endpoints (NOT the role-locked tx-groups endpoint).
 */
const OrgScopedList: React.FC<OrgScopedListProps> = ({
  organizationId,
  httpFetchDataFn,
  columns,
  emptyHeading = "No records found",
}) => {
  const { rows, rowCount, loading, hasMore, fetchData, currentPage } = infiniteScrollHook({
    httpFetchDataFn,
    overrideReqId: organizationId,
  });

  const cellValue = (row: any, col: OrgScopedColumn) => {
    if (col.render) return col.render(row);
    const formatted = row?.formatted?.[col.key];
    const value = formatted ?? row?.[col.key];
    return value === undefined || value === null || value === "" ? "—" : value;
  };

  if (loading && (!rows || rows.length === 0)) {
    return (
      <div className="platform-loadmore">
        <SimpleSpinner />
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <EmptyState
        icon={GetImages.emptyStateData}
        heading={emptyHeading}
        isButton={false}
        isBorder={false}
      />
    );
  }

  return (
    <div className="platform-tab-section">
      <div className="platform-table-wrapper">
        <table className="platform-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row: any, idx: number) => (
              <tr key={row?.id ?? idx}>
                {columns.map((col) => (
                  <td key={col.key}>{cellValue(row, col)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hasMore && (
        <div className="platform-loadmore">
          <button
            className="btn btn-outline-primary btn-sm"
            disabled={loading}
            onClick={() => fetchData(currentPage + 1)}
          >
            {loading ? "Loading…" : `Load more (${rows.length} of ${rowCount})`}
          </button>
        </div>
      )}
    </div>
  );
};

export default OrgScopedList;
